import type { Notification } from './types.ts'

/** One row on the page: the newest notification plus any identical repeats folded into it. */
export interface NotificationRow {
  /** The newest notification — its time, href and detail page stand for the row. */
  lead: Notification
  /** Every notification folded into this row, newest first (lead included). */
  ids: string[]
  count: number
  read: boolean
}

export interface NotificationSection {
  label: string
  rows: NotificationRow[]
}

const DAY = 24 * 60 * 60 * 1000

function sectionLabel(createdAt: string, now: Date): string {
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const t = new Date(createdAt).getTime()
  if (t >= startOfToday) return 'Today'
  if (t >= startOfToday - DAY) return 'Yesterday'
  if (t >= startOfToday - 6 * DAY) return 'This week'
  if (t >= startOfToday - 13 * DAY) return 'Last week'
  return 'Earlier'
}

// Repeats are the same kind of notice from the same org with the same title,
// e.g. a team asking five times to leave the same competition.
const repeatKey = (n: Notification) => `${n.type}|${n.org.slug}|${n.title}`

/**
 * Turns the newest-first notification list into dated sections. Order stays
 * strictly newest to oldest: a folded row sits where its newest copy was.
 */
export function buildSections(notifications: Notification[], now = new Date()): NotificationSection[] {
  const sorted = [...notifications].sort((a, b) => b.createdAt.localeCompare(a.createdAt))
  const rowsByKey = new Map<string, NotificationRow>()
  const rows: NotificationRow[] = []
  for (const n of sorted) {
    const existing = rowsByKey.get(repeatKey(n))
    if (existing) {
      existing.ids.push(n.id)
      existing.count++
      existing.read = existing.read && n.read
      continue
    }
    const row = { lead: n, ids: [n.id], count: 1, read: n.read }
    rowsByKey.set(repeatKey(n), row)
    rows.push(row)
  }

  const sections: NotificationSection[] = []
  for (const row of rows) {
    const label = sectionLabel(row.lead.createdAt, now)
    const last = sections[sections.length - 1]
    if (last?.label === label) last.rows.push(row)
    else sections.push({ label, rows: [row] })
  }
  return sections
}
